import type {
  KindOfResearch as KindOfResearchType,
  Project,
  ResearchConductType as ResearchConductTypeType,
} from '@/api/core-api/types.gen';

import { TaskType } from '@/api/core-api/types.gen';
import { KindOfResearch, ResearchConductType } from '@/api/enums';

/** Check if the study type is an AI-moderated interview */
export function isAiModStudy(
  kor?: KindOfResearchType | number | null,
): boolean {
  return kor === KindOfResearch.AI_MODERATED_INTERVIEW;
}

/** Check if the study is conducted in person (vs remote) */
export function isInPersonStudy(
  rct?: ResearchConductTypeType | number | null,
): boolean {
  return rct === ResearchConductType.IN_PERSON;
}

/** Quantitative (survey-like) study types that use external task/survey settings */
export function isQuantitativeStudy(
  kor?: KindOfResearchType | number | null,
): boolean {
  return (
    kor === KindOfResearch.SURVEY ||
    kor === KindOfResearch.UNMODERATED_STUDY ||
    kor === KindOfResearch.DIARY_STUDY
  );
}

/**
 * Quotas can only be configured for quantitative studies that also have
 * auto-invite enabled. (Viewing quota progress is gated on a quota existing,
 * not on this — see the Analytics tab in `details.tsx`.)
 */
export function isQuotaEligible(
  project: Pick<Project, 'kindOfResearch' | 'automations'>,
): boolean {
  return (
    isQuantitativeStudy(project.kindOfResearch) && !!project.automations?.invite
  );
}

/**
 * Auto-invite only applies to study types that can invite without a researcher
 * in the loop: quantitative (survey-like) studies and AI-moderated interviews.
 * Moderated studies schedule against the researcher's own calendar, so there is
 * nothing to automate.
 *
 * The launch flow expresses the same policy structurally rather than through
 * this helper — it renders a different section per branch
 * (`QuantitativeSettingsSection` for quantitative, a bare summary section for
 * AI-mod, both in `-launch/launch-content.tsx`). Keep the two in step: if a
 * study type gains or loses auto-invite there, update this predicate too.
 */
export function supportsAutoInvite(
  kor?: KindOfResearchType | number | null,
): boolean {
  return isQuantitativeStudy(kor) || isAiModStudy(kor);
}

/** Diary studies are the only study type that can be paid again (RX-197). */
export function isDiaryStudy(kindOfResearch?: KindOfResearch | null): boolean {
  return kindOfResearch === KindOfResearch.DIARY_STUDY;
}

/** Integration task types that drive an external "online task" a participant completes. */
const ONLINE_TASK_TYPES: ReadonlySet<TaskType> = new Set([
  TaskType.LOOKBACK,
  TaskType.SURVEYMONKEY,
  TaskType.CUSTOM_URL,
]);

/**
 * Online-task projects run an external integration task (Lookback recording,
 * SurveyMonkey survey, or a custom URL) that participants complete on their own.
 * These are the only projects that get a Completes tab.
 */
export function isOnlineTaskProject(project: Pick<Project, 'task'>): boolean {
  const type = project.task?.type;
  return type !== undefined && ONLINE_TASK_TYPES.has(type);
}

export type ProjectFlow = 'moderated' | 'ai-moderated' | 'survey' | 'external';

export function getProjectFlow(project: Project): ProjectFlow {
  // TODO: do Calendly! `isExternalModeratedProject` flag (Calendly-style external
  // lifecycle) is not yet surfaced in the OpenAPI types. The `external` branch
  // exists so the table can hide actions when the flag becomes available.
  if (isQuantitativeStudy(project.kindOfResearch)) return 'survey';
  if (isAiModStudy(project.kindOfResearch)) return 'ai-moderated';
  return 'moderated';
}

/**
 * Moderated study types — live sessions (remote or in-person) that the
 * researcher schedules against their own availability.
 */
export function isModeratedStudy(
  kindOfResearch?: KindOfResearch | null,
): boolean {
  return (
    kindOfResearch === KindOfResearch.ONE_ON_ONE ||
    kindOfResearch === KindOfResearch.FOCUS_GROUP ||
    kindOfResearch === KindOfResearch.IN_RESPONDENT_HOME ||
    kindOfResearch === KindOfResearch.IN_RESPONDENT_OFFICE
  );
}

/** The project fields the availability gate depends on. */
type AvailabilityGateProject = Pick<
  Project,
  'kindOfResearch' | 'externalResearcher'
>;

/**
 * External moderated projects are run by an external researcher (via the public
 * API) on a moderated study. They manage scheduling outside Respondent, so the
 * in-app availability gate doesn't apply.
 */
export function isExternalModeratedProject(
  project: AvailabilityGateProject,
): boolean {
  return (
    !!project.externalResearcher?.researcherId &&
    isModeratedStudy(project.kindOfResearch)
  );
}

/**
 * Whether a project requires the researcher to establish availability (an
 * active calendar) before inviting respondents. True only for in-house
 * moderated studies: survey-like and AI-moderated studies don't schedule
 * against a calendar, and external researchers manage their own scheduling.
 */
export function isAvailabilityRequired(
  project: AvailabilityGateProject,
): boolean {
  return (
    isModeratedStudy(project.kindOfResearch) &&
    !isExternalModeratedProject(project)
  );
}
